import { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { socialLinks } from '../config/socialLinks';
import {
  cancelCompanionRequest,
  fetchCompanionRequests,
  removeCompanion,
  respondToCompanionRequest,
} from '../api/companions';
import CompanionsSection from '../components/companions/CompanionsSection';
import InviteCompanionSheet from '../components/companions/InviteCompanionSheet';
import RemoveCompanionDialog from '../components/companions/RemoveCompanionDialog';
import TavernChatPanel from '../components/chat/TavernChatPanel';
import DiscordIcon from '../components/tavern/DiscordIcon';
import { useToast } from '../components/tavern/ToastProvider';
import {
  companionRequestCancelMessage,
  companionRequestResponseMessage,
} from '../utils/apiErrorMessages';

const formatRequestDate = (value, language) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(language, {
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const RequestsSection = ({ direction = 'incoming', onChanged }) => {
  const { i18n, t } = useTranslation();
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const language = i18n.resolvedLanguage || i18n.language;
  const incoming = direction === 'incoming';
  const headingKey = incoming
    ? 'friends.requests.receivedTitle'
    : 'friends.requests.sentTitle';
  const emptyTitleKey = incoming
    ? 'friends.requests.receivedEmptyTitle'
    : 'friends.requests.sentEmptyTitle';
  const emptySubtitleKey = incoming
    ? 'friends.requests.receivedEmptySubtitle'
    : 'friends.requests.sentEmptySubtitle';

  useEffect(() => {
    let mounted = true;

    const loadRequests = () => {
      setLoading(true);
      fetchCompanionRequests({ direction })
        .then((data) => {
          if (mounted) setRequests(data.items || []);
        })
        .catch(() => {
          if (mounted) toast.error(t('friends.requests.loadError'));
        })
        .finally(() => {
          if (mounted) setLoading(false);
        });
    };

    loadRequests();

    const handleServerEvent = (event) => {
      if (['notice.created', 'notice.updated'].includes(event.detail?.type)) {
        loadRequests();
      }
    };
    const handleRevalidate = () => loadRequests();

    window.addEventListener('thetavern:server-event', handleServerEvent);
    window.addEventListener('thetavern:revalidate', handleRevalidate);
    return () => {
      mounted = false;
      window.removeEventListener('thetavern:server-event', handleServerEvent);
      window.removeEventListener('thetavern:revalidate', handleRevalidate);
    };
  }, [direction, t, toast]);

  const respond = async (requestId, decision) => {
    setBusyId(requestId);
    try {
      await respondToCompanionRequest(requestId, decision);
      setRequests((current) => current.filter((request) => request.id !== requestId));
      toast.success(
        decision === 'accepted'
          ? t('friends.requests.accepted')
          : t('friends.requests.declined')
      );
      onChanged?.();
    } catch (error) {
      toast.error(companionRequestResponseMessage(error, t));
      if ([404, 409].includes(Number(error?.response?.status))) {
        setRequests((current) => current.filter((request) => request.id !== requestId));
        onChanged?.();
      }
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (requestId) => {
    setBusyId(requestId);
    try {
      await cancelCompanionRequest(requestId);
      setRequests((current) => current.filter((request) => request.id !== requestId));
      toast.success(t('friends.requests.canceled'));
      onChanged?.();
    } catch (error) {
      toast.error(companionRequestCancelMessage(error, t));
      if ([404, 409].includes(Number(error?.response?.status))) {
        setRequests((current) => current.filter((request) => request.id !== requestId));
        onChanged?.();
      }
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography color="text.secondary">{t('friends.requests.loading')}</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Typography
          component="h2"
          variant="subtitle2"
          color="secondary.dark"
          sx={{ mb: 2, fontSize: { xs: '0.82rem', sm: '0.9rem' } }}
        >
          {t(headingKey, { count: requests.length })}
        </Typography>
        {requests.length === 0 ? (
          <Box
            sx={{
              p: 2,
              border: '1px dashed',
              borderColor: 'rgba(215, 160, 75, 0.56)',
              borderRadius: 2,
              bgcolor: 'rgba(255,250,240,0.58)',
            }}
          >
            <Typography variant="h4">{t(emptyTitleKey)}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t(emptySubtitleKey)}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {requests.map((request) => (
              <Stack
                key={request.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'rgba(255,250,240,0.58)',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar src={request.user.avatarUrl || undefined}>
                    {request.user.displayName?.[0] || request.user.username?.[0]}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h4" noWrap>
                      {request.user.displayName}
                    </Typography>
                    <Typography color="text.secondary" noWrap>
                      {incoming
                        ? t('friends.requests.wantsToJoin')
                        : t('friends.requests.sentTo')}
                    </Typography>
                    {request.requestedAt ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('friends.requests.requestedOn', {
                          date: formatRequestDate(request.requestedAt, language),
                        })}
                      </Typography>
                    ) : null}
                  </Box>
                </Stack>
                <Stack
                  direction={incoming ? 'row' : 'column'}
                  spacing={1}
                  justifyContent="flex-end"
                  alignItems={incoming ? 'center' : { xs: 'stretch', sm: 'flex-end' }}
                  sx={
                    incoming
                      ? undefined
                      : {
                          minWidth: { sm: 210 },
                          p: 1,
                          border: '1px solid rgba(15, 76, 69, 0.18)',
                          borderRadius: 2,
                          bgcolor: 'rgba(15, 76, 69, 0.05)',
                        }
                  }
                >
                  {incoming ? (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        disabled={busyId === request.id}
                        onClick={() => respond(request.id, 'declined')}
                      >
                        {t('friends.requests.decline')}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CheckIcon />}
                        disabled={busyId === request.id}
                        onClick={() => respond(request.id, 'accepted')}
                      >
                        {t('friends.requests.accept')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-end' } }}
                      >
                        {t('friends.requests.waitingForResponse')}
                      </Typography>
                      <Button
                        data-testid="sent-request-cancel-button"
                        variant="contained"
                        color="secondary"
                        startIcon={<CloseIcon />}
                        disabled={busyId === request.id}
                        onClick={() => cancel(request.id)}
                        sx={{ alignSelf: 'stretch' }}
                      >
                        {t('friends.requests.cancel')}
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const FriendsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [companionsRefreshKey, setCompanionsRefreshKey] = useState(0);
  const [chatContext, setChatContext] = useState(null);
  const [companionToRemove, setCompanionToRemove] = useState(null);
  const [removingCompanion, setRemovingCompanion] = useState(false);
  const handleInviteToNight = useCallback(
    (companion) => {
      if (!companion?.id) {
        setInviteOpen(true);
        return;
      }

      navigate(`/nights/new?companionId=${encodeURIComponent(companion.id)}`);
    },
    [navigate]
  );
  const handleOpenProfile = useCallback(
    (companion) => {
      if (companion?.username) {
        navigate(`/u/${encodeURIComponent(companion.username)}`);
      }
    },
    [navigate]
  );
  const openDirectChat = useCallback(
    (companion) => {
      setChatContext({
        type: 'direct',
        userId: companion.id,
        title: t('chat.context.directTitle', { name: companion.displayName }),
        subtitle: t('chat.context.directSubtitle'),
        participants: [companion],
      });
    },
    [t]
  );
  const openGroupChat = useCallback(() => {
    setChatContext({
      type: 'friends',
      title: t('chat.context.friendsTitle'),
      subtitle: t('chat.context.friendsSubtitle'),
    });
  }, [t]);

  useEffect(() => {
    const refreshCompanions = () => setCompanionsRefreshKey((key) => key + 1);

    window.addEventListener('thetavern:revalidate', refreshCompanions);
    return () => window.removeEventListener('thetavern:revalidate', refreshCompanions);
  }, []);

  const handleRemoveCompanion = async () => {
    if (!companionToRemove?.id || removingCompanion) return;

    setRemovingCompanion(true);
    try {
      await removeCompanion(companionToRemove.id);
      toast.success(t('companions.feedback.removed'));
      setCompanionToRemove(null);
      setCompanionsRefreshKey((key) => key + 1);
    } catch (error) {
      toast.error(
        [404, 409].includes(Number(error?.response?.status))
          ? t('companions.feedback.removeAlreadyDone')
          : t('companions.feedback.removeError')
      );
      if ([404, 409].includes(Number(error?.response?.status))) {
        setCompanionToRemove(null);
        setCompanionsRefreshKey((key) => key + 1);
      }
    } finally {
      setRemovingCompanion(false);
    }
  };

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
            {t('friends.eyebrow')}
          </Typography>
          <Typography variant="h1">{t('friends.title')}</Typography>
        </Box>
      </Stack>

      <Box>
        <Typography
          component="h2"
          variant="subtitle2"
          color="secondary.dark"
          sx={{ mb: 2, fontSize: { xs: '0.82rem', sm: '0.9rem' } }}
        >
          {t('friends.sections.companionsTitle')}
        </Typography>
        <CompanionsSection
          key={companionsRefreshKey}
          limit={6}
          mode="list"
          onChat={openDirectChat}
          onInvite={handleInviteToNight}
          onOpenProfile={handleOpenProfile}
          onRemove={setCompanionToRemove}
        />
      </Box>

      <Card
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderColor: 'rgba(15, 76, 69, 0.22)',
          background:
            'linear-gradient(135deg, rgba(15,76,69,0.08), rgba(236,170,65,0.12))',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" color="secondary.dark">
                {t('chat.entry.friendsEyebrow')}
              </Typography>
              <Typography variant="h3" sx={{ mt: 0.5 }}>
                {t('chat.entry.friendsTitle')}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                {t('chat.entry.friendsCopy')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={openGroupChat}
              sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              {t('chat.actions.open')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <RequestsSection
        direction="incoming"
        onChanged={() => setCompanionsRefreshKey((key) => key + 1)}
      />

      <RequestsSection
        direction="outgoing"
        onChanged={() => setCompanionsRefreshKey((key) => key + 1)}
      />

      <Card
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderColor: 'rgba(215, 160, 75, 0.48)',
          bgcolor: 'rgba(255, 250, 240, 0.86)',
          boxShadow: '0 18px 40px -32px rgba(6, 37, 34, 0.55)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2.25, sm: 3 }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack
              spacing={1.25}
              alignItems={{ xs: 'center', sm: 'flex-start' }}
              sx={{ minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}
            >
              <Box
                data-testid="discord-community-emblem"
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  display: 'grid',
                  flex: '0 0 auto',
                  placeItems: 'center',
                  color: 'primary.main',
                  bgcolor: 'rgba(215, 160, 75, 0.16)',
                  border: '1px solid rgba(215, 160, 75, 0.42)',
                  boxShadow: '0 14px 28px -22px rgba(6, 37, 34, 0.62)',
                }}
              >
                <DiscordIcon />
              </Box>
              <Box data-testid="discord-community-heading" sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" color="secondary.dark">
                  {t('social.discord.cardEyebrow')}
                </Typography>
                <Typography variant="h3" sx={{ mt: 0.25 }}>
                  {t('social.discord.cardTitle')}
                </Typography>
              </Box>
              <Typography color="text.secondary" sx={{ maxWidth: 620 }}>
                {t('social.discord.cardCopy')}
              </Typography>
            </Stack>
            <Button
              component="a"
              href={socialLinks.discord}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<DiscordIcon />}
              aria-label={t('social.discord.ariaLabel')}
              sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, whiteSpace: 'nowrap' }}
            >
              {t('social.discord.cta')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <InviteCompanionSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
      <RemoveCompanionDialog
        companion={companionToRemove}
        loading={removingCompanion}
        open={Boolean(companionToRemove)}
        onClose={() => {
          if (!removingCompanion) setCompanionToRemove(null);
        }}
        onConfirm={handleRemoveCompanion}
      />
      <TavernChatPanel
        context={chatContext || {}}
        open={Boolean(chatContext)}
        onClose={() => setChatContext(null)}
      />
    </Stack>
  );
};

export default FriendsPage;
